import { useState } from 'react';

import { IconButton } from '@mui/material';

import { useParams } from 'react-router';

import { TPersonaDto } from '@app-velocity/av-api-v2';

import { useI18n } from '@app-velocity/i18n';
import { EllipsisIcon, EditIcon, RemoveIcon } from '@app-velocity/icons';
import { MessageBus } from '@app-velocity/message-bus';
import { ContextMenu as UIContextMenu } from '@app-velocity/ui';

import { useDeletePersonaMutation } from '../../../../../../query';
import { usePersonaManagement } from '../../../../../../hooks';

const messageBus = new MessageBus('system');

export type PersonaRowActionsProps = {
  persona: TPersonaDto;
};

export const PersonaRowActions = ({
  persona,
}: PersonaRowActionsProps) => {
  const { t } = useI18n();

  const { teamId } = useParams<{ teamId: string }>();
  const { openPersonaModal } = usePersonaManagement(teamId!);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const { mutate: deletePersona } = useDeletePersonaMutation(teamId!);

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleEdit = () => {
    if (!persona.id) return;

    openPersonaModal(persona.id);
    closeMenu();
  };

  const confirmDelete = () => {
    if (!persona.id) return;

    messageBus.publish('modal:action-confirmation:open', {
      title: 'personas:delete.title',
      description: t('personas:delete.description', {
        name: persona.name,
      }),
      onModalCloseCallback: (decision) => {
        if (decision === 'OK' && persona.id) {
          deletePersona(persona.id);
        }
      },
      confirmButtonText: 'personas:delete.confirm',
      cancelButtonText: 'shared:cancel',
    });
    closeMenu();
  };

  return (
    <>
      <IconButton onClick={openMenu}>
        <EllipsisIcon />
      </IconButton>
      <UIContextMenu
        anchor={menuAnchor}
        closeMenu={closeMenu}
        color="light"
        variant="compact"
      >
        <UIContextMenu.Item onClick={handleEdit}>
          <EditIcon />
          {t('personas:list.edit')}
        </UIContextMenu.Item>
        <UIContextMenu.Divider />
        <UIContextMenu.Item variant="danger" onClick={confirmDelete}>
          <RemoveIcon />
          {t('personas:list.delete')}
        </UIContextMenu.Item>
      </UIContextMenu>
    </>
  );
};
